import React, { createContext, useContext, useEffect, useState } from "react";
import { AgglomerationI } from "@/types/base";
import { getAgglomeration } from "@/api/base";
import { useAgglomeration } from "@/store/agglomeration";
import { useTelegramScreen } from "@/utils/hooks/useTelegramScreen";
import { useSelectedHexGroupStore } from "@/store/selectedHexGroup";
import { useUserDataContext } from "@/store/UserProvider";

interface AgglomerationsContextValue {
  agglomerations: AgglomerationI[];
  agglomerationsLoading: boolean;
}

const AgglomerationsContext = createContext<
  AgglomerationsContextValue | undefined
>(undefined);

export const AgglomerationsProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  useTelegramScreen();

  const { user } = useUserDataContext();

  const { setSelectedAgglomeration } = useAgglomeration();
  const [agglomerations, setAgglomerations] = useState<AgglomerationI[]>([]);
  const [agglomerationsLoading, setAgglomerationsLoading] = useState(true);

  const setSelectedHexGroup = useSelectedHexGroupStore(
    (s) => s.setSelectedHexGroup
  );

  const loadData = async () => {
    if (!user?.telegram_id) return;
    setAgglomerationsLoading(true);
    try {
      const res = await getAgglomeration();
      setAgglomerations(res);

      const userAgglomeration = res.find(
        (r) => r.id === user?.agglomeration_id
      );
      const userHexGroup = userAgglomeration?.hexgroups.find(
        (r) => r.id === user?.hexgroup_id
      );

      if (userAgglomeration && userHexGroup) {
        setSelectedAgglomeration(userAgglomeration);
        setSelectedHexGroup(userHexGroup);
      }
    } catch (e) {
      console.log(e);
    } finally {
      setAgglomerationsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [user?.telegram_id]);

  return (
    <AgglomerationsContext.Provider
      value={{ agglomerations, agglomerationsLoading }}
    >
      {children}
    </AgglomerationsContext.Provider>
  );
};

export const useAgglomerationsDataContext = (): AgglomerationsContextValue => {
  const ctx = useContext(AgglomerationsContext);
  if (!ctx) {
    throw new Error(
      "useAgglomerationsDataContext must be used within AgglomerationsProvider"
    );
  }
  return ctx;
};
